import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { safelyConsoleError } from '../../utils/safely-console-error';
import { confirmCallbackTrigger } from './confirm-callback-trigger';

interface UseJourneyTransitionCallbackOptions {
	/**
	 * Function to call when user leaves a journey - e.g. clearing state, triggering API calls to remove a transaction etc.
	 */
	callback: () => void | Promise<void>;
	/**
	 * Path pattern to match routes - e.g. `/cancellation` - we handle this with a `url.includes()` method
	 */
	pathPattern: string;
}

/**
 * Hook that triggers a callback function when the user leaves a given journey, specifically on transition.
 * @param options.callback Function to call when user leaves a journey - e.g. clearing state, triggering API calls to remove a transaction etc.
 * @param options.pathPattern Path pattern to match routes - e.g. `/cancellation` - we handle this with a `url.includes()` method
 * @warning
 *   - IMPORTANT limitations -
 *     - This hook only triggers on route changes, not on component unmount. This should be an acceptance when implementing this as part of your solution
 *     - Scenarios
 *       - The callback will run when
 *         - The user uses an internal Link to access a url outside the journey
 *         - The user navigates away from the journey using the browser's back/forward buttons
 *       - The callback will NOT run when
 *         - A user manually inputs a new url while on the journey to navigate away
 *         - Browser lifecycle related unmounts - a user closes the browser tab; a user closes the browser etc.
 * 	- As a result, this level of cleanup should be a _nice to have_ rather than a strict requirement for your feature.
 */
export const useJourneyTransitionCallback = ({ callback, pathPattern }: UseJourneyTransitionCallbackOptions) => {
	const location = useLocation();
	const currentUrl = location.pathname;
	const previousUrlRef = useRef<string | null>(null); // compared with currentUrl
	const pendingRef = useRef(false); // tracks if analysis/callback are in progress

	// Main route listener - triggers cleanup when leaving journey
	useEffect(() => {
		if (pendingRef.current) return;
		// Check routes to see if we're off the journey
		const transitionState = {
			previousUrl: previousUrlRef.current,
			currentUrl,
			pathPattern,
		};

		const shouldTriggerCallback = confirmCallbackTrigger(transitionState);

		if (shouldTriggerCallback) {
			const executeCallback = async () => {
				pendingRef.current = true;

				try {
					await callback();
				} catch (error) {
					safelyConsoleError('Failed execute callback on journey exit:', error); // only logs in dev
				} finally {
					pendingRef.current = false;
				}
			};

			executeCallback();
		}

		previousUrlRef.current = currentUrl;

		return () => {
			pendingRef.current = false;
		};
	}, [currentUrl, callback, pathPattern]);
};
